import { Provider, ReflectiveInjector } from 'injection-js';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { PosturinnClientCoreModule } from '../../@posturinn/client-react';
import { IWebConsoleLogHandlerConfiguration, MobizCommonCoreModule } from '../../@posturinn/common-react/core';
import { MobizCommonHttpAxiosModule } from '../../@posturinn/common-react/http-axios';
import { MobizCommonNotificationsModule } from '../../@posturinn/common-react/notifications';
import { MobizCommonSettingsModule } from '../../@posturinn/common-react/settings';
import { setInitialDataFulfilled } from '../../actions/pageActions';
import { initilizeInjector } from '../../actions/userProfileActions';
import EntryMask from '../../components/entryMask';
import ErrorBoundary from '../../components/errorBoundary';
import Footer from '../../components/footer-v2';
import Header from '../../components/header-v2';
import MainLoader from '../../components/loading/mainLoader';
import Modals from '../../components/modals';
import Payment3DSecure from '../../components/payments3DSecure/payment3DSecure';
import GlobalStyle from '../../constants/globalStyle';
import theme from '../../constants/theme';
import { initDatadog } from '../../helpers/datadog-init';
import flatten from '../../helpers/flatten';
import { CONFIGURATION_SERVICE_PROVIDERS } from '../../helpers/mobizHelpers/configurations';
import ScrollToTop from '../../helpers/scrollToTop';
import { ReactComponent as Logo } from '../../icons/logo.svg';
import INavigationItem from '../../interfaces/navigation/navigationItem';
import IPageViewModel from '../../interfaces/umbracoNodes/pageViewModel';
import Routes from '../../routes';
import store from '../../store';

const StyledAppWrapper = styled.div`
	overflow-x: clip;
	position: relative;
`;

interface Props {
	page: IPageViewModel;
	navigation: INavigationItem[];
	settings: IPageViewModel;
	appFrame: IPageViewModel;
}

const App: FC<Props> = ({ page, navigation, settings, appFrame }) => {
	useEffect(() => {
		initDatadog();
	}, []);

	const injector = ReflectiveInjector.resolveAndCreate([
		// NOTE: The configuration providers needs to be defined first.
		CONFIGURATION_SERVICE_PROVIDERS,
		MobizCommonCoreModule.PROVIDERS,
		MobizCommonHttpAxiosModule.PROVIDERS,
		PosturinnClientCoreModule.PROVIDERS,
		MobizCommonNotificationsModule.PROVIDERS,
		MobizCommonSettingsModule.PROVIDERS,
	]);

	store.dispatch(setInitialDataFulfilled({ page, navigation, settings, appFrame }));
	store.dispatch(initilizeInjector(injector));
	const routes: INavigationItem[] = [];
	flatten({ Children: navigation }, 'Children', routes);

	return (
		<ErrorBoundary fallback={<FullPageFallback />}>
			<ReduxProvider store={store}>
				<Payment3DSecure>
					<ThemeProvider theme={theme}>
						<StyledAppWrapper>
							<GlobalStyle />
							<EntryMask />
							<Router>
								<Header />
								<Modals />
								<MainLoader />
								<ScrollToTop>
									<Routes routes={routes} />
								</ScrollToTop>
								<Footer />
							</Router>
						</StyledAppWrapper>
					</ThemeProvider>
				</Payment3DSecure>
			</ReduxProvider>
		</ErrorBoundary>
	);
};

// This fallback component cannot use any components that depend on any provider because
//   the ErrorBoundary in this file wraps all the providers. This includes almost all of our components.
//   This means that the fallback components in other instances of ErrorBoundary can use any components since they're inside the provider context.
const FullPageFallback = () => {
	const { t } = useTranslation('common');
	return (
		<StyledDiv>
			<Logo></Logo>
			<h1>{t('error.error-500-unknown')}</h1>
			<p>{t('error.error-try-refresh')}</p>
			<StyledButton type="button" onClick={() => window.location.reload()}>
				{t('refresh')}
			</StyledButton>
		</StyledDiv>
	);
};

const StyledDiv = styled.div`
	display: grid;
	align-content: center;
	grid-auto-rows: min-content;
	justify-items: center;
	height: 100vh;
	font-family: 'Arimo Medium', Arial, sans-serif;
	color: #3c3c3c;
`;

const StyledButton = styled.button`
	color: #fff;
	background-color: #ff0000;
	font-family: inherit;
	font-size: 1.4rem;
	padding: 1rem;
	border-radius: 8px;
	border: 0;
	margin-top: 1rem;
	&:hover {
		cursor: pointer;
		background-color: #df0000;
	}
`;

export default App;
